import styles from './OutboundCallModal.module.css';
import { useState } from 'react';

import {
  Banner,
  Button,
  ButtonGroup,
  ButtonToggle,
  Modal,
  Stack,
} from '@servicetitan/design-system';
import OutboundModalKeypad from './components/OutboundModalKeypad/OutboundModalKeypad';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import OutboundModalEmployeeTable from './components/OutboundModalEmployeeTable/OutboundModalEmployeeTable';
import OutboundModalQueues from './components/OutboundModalQueues';
import useAuth from '@modules/AuthModule/hooks/useAuth';

interface OutboundCallModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

/**
 * Returns a modal component that allow users to call employees, queues, or an external number.
 *
 * The modal consists of 3 "tabs": Employees, Queue, and Keypad, and each of them allows the user
 * to make a call in a different way.
 * - Employees: displays a list of employees that the user can call
 * - Queue: displays a list of queues that the user can call
 * - Keypad: the user can dial in a number and call it
 *
 * @param props: OutboundCallModalProps
 * @returns
 */
const OutboundCallModal: React.FC<OutboundCallModalProps> = ({
  open,
  setOpen,
}) => {
  // states
  const [number, setNumber] = useState('');
  const [selectedTab, setSelectedTab] = useState('Employees');
  const { user } = useAuth();
  // controls
  const tabStates = {
    keypadSelected: selectedTab === 'Keypad',
    employeesSelected: selectedTab === 'Employees',
    queueSelected: selectedTab === 'Queue',
  };

  // TODO: revisit the validation for dialpad number to support more than 10 digits international numbers
  const callButtonEnabled = number.length > 0 && user?.outboundCallsAllowed;

  function handleClose() {
    setNumber('');
    setOpen(false);
  }

  const handleTabChange = (val: string) => {
    setNumber('');
    setSelectedTab(val);
  };

  return (
    <Modal
      className={styles.outboundCallModal}
      open={open}
      focusTrapOptions={{ disabled: true }}
      portal={false}
      title='Make a Call'
    >
      <Stack direction='column' alignItems='center' spacing={2}>
        <ButtonToggle
          className={styles.outboundCallModalTabs}
          onChange={handleTabChange}
          options={[
            {
              text: 'Employees',
              value: 'Employees',
              selected: tabStates.employeesSelected,
            },
            {
              text: 'Queue',
              value: 'Queue',
              selected: tabStates.queueSelected,
            },
            {
              text: 'Keypad',
              value: 'Keypad',
              selected: tabStates.keypadSelected,
            },
          ]}
        />
        {/* Display components depending on the tab selected by the user */}
        {tabStates.employeesSelected && (
          <OutboundModalEmployeeTable setNumber={setNumber} />
        )}
        {tabStates.queueSelected && (
          <OutboundModalQueues setNumber={setNumber} />
        )}
        {tabStates.keypadSelected && (
          <>
            {!user?.outboundCallsAllowed && (
              <Banner title={''} status='warning' icon={true}>
                Click to call is unavailable. Please complete the following
                steps to enable outbound calls:
                <ul>
                  <li>A basic or advanced user seat</li>
                  <li>Assigned to a phone number</li>
                  <li>E911 address configured</li>
                </ul>
              </Banner>
            )}
            <OutboundModalKeypad number={number} setNumber={setNumber} />
          </>
        )}
        <div className={styles.keypadFooterSpace} />
        {/* Spacer to match the design */}
      </Stack>
      <Modal.Footer className={styles.outboundCallModalFooter}>
        <ButtonGroup>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            primary
            disabled={!callButtonEnabled}
            onClick={() => {
              SoftPhoneModule.callNumber(number);
              handleClose();
            }}
          >
            Call
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default OutboundCallModal;
